import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Upload, Loader2, CheckCircle, Send, Eye } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { getChannelIcon } from '@/utils/channels';
import type { Channel } from '@/services/business/calendar';
import type { StaffVideoTask } from '@/services/data/staffVideoContent';
import { getDerushFiles, getSubfolder } from '@/services/data/monteurActions';
import { getS3DownloadUrl } from '@/services/data/s3Files';
import { advanceVEStatus, hasDerushFiles } from '@/services/data/veActions';
import { MonteurDetailDialog } from './MonteurDetailDialog';
import { VEUploadDialog } from './VEUploadDialog';
import { VEReviewDialog } from './VEReviewDialog';
import { SignedImage } from '@/components/ui/SignedImage';
import { toast } from 'sonner';


const CONTENT_TYPE_LABELS: Record<string, string> = {
  standard: 'Standard',
  mythbuster: 'Mythbuster',
  hot_take: 'Hot Take',
  advice: 'Advice',
  storytelling: 'Storytelling',
  liste: 'Liste',
  decryptage: 'Décryptage',
  reaction: 'Réaction',
  profil: 'Profil',
  tutorial: 'Tutorial',
  showcase: 'Showcase',
  testimonial: 'Testimonial',
  multihook: 'Multihook',
};

type StaffRole = 'video_editor' | 'monteur' | null;

interface StaffVideoCardProps {
  readonly task: StaffVideoTask;
  readonly staffRole?: StaffRole;
}

const getStatusLabel = (status: string | null, staffRole?: StaffRole): string => {
  if (staffRole === 'monteur') {
    switch (status) {
      case 'editing_pending':
        return 'Bout à bout prêt';
      case 'editing_review':
        return 'En validation';
      case 'client_feedback':
        return 'Retour client reçu';
      default:
        return status || 'Inconnu';
    }
  }
  switch (status) {
    case 'shot':
      return 'Tournage validé · À dérusher';
    case 'derush_pending':
      return 'Dérush en cours';
    case 'derush_done':
      return 'Dérush terminé';
    case 'editing_pending':
      return 'Montage en cours';
    case 'editing_review':
      return 'À valider';
    case 'redelivery_review':
      return 'Relivraison à valider';
    default:
      return status || 'Inconnu';
  }
};

const getStatusColor = (status: string | null, staffRole?: StaffRole): string => {
  if (staffRole === 'monteur') {
    switch (status) {
      case 'editing_pending':
        return 'text-green-400';
      case 'editing_review':
        return 'text-warning';
      case 'client_feedback':
        return 'text-pink-400';
      default:
        return 'text-white/50';
    }
  }
  switch (status) {
    case 'shot':
      return 'text-orange-400';
    case 'derush_pending':
      return 'text-yellow-400';
    case 'derush_done':
      return 'text-green-400';
    case 'editing_pending':
      return 'text-yellow-400';
    case 'editing_review':
      return 'text-amber-400';
    case 'redelivery_review':
      return 'text-fuchsia-400';
    default:
      return 'text-white/50';
  }
};

const getDeadlineColor = (dateStr: string | null): string => {
  if (!dateStr) return 'text-white/50';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'text-red-400';
  } else if (diffDays <= 2) {
    return 'text-orange-400';
  }
  return 'text-white/50';
};

// Helper function to get the card border class
const getCardBorderClass = (isDone: boolean, isExpanded: boolean): string => {
  if (isDone) {
    return 'border-emerald-500/30 opacity-60 cursor-default';
  }
  const expandedClass = isExpanded ? 'border-warning/40' : 'border-white/10 hover:border-warning/30';
  return `cursor-pointer active:scale-[0.98] ${expandedClass}`;
};

// Helper function to get virality badge class
const getViralityBadgeClass = (virality: string | null): string => {
  if (virality === 'V3') {
    return 'border-emerald-500/40 text-emerald-400';
  }
  if (virality === 'V2') {
    return 'border-yellow-500/40 text-yellow-400';
  }
  return 'border-white/20 text-white/40';
};

export function StaffVideoCard({ task, staffRole }: Readonly<StaffVideoCardProps>) {
  const navigate = useNavigate();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [veUploadOpen, setVEUploadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [hasFiles, setHasFiles] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const [monteurDetailOpen, setMonteurDetailOpen] = useState(false);
  const [veReviewOpen, setVEReviewOpen] = useState(false);

  const thumbnail = task.video_1_thumbnail || task.thumbnail_url;
  const clientName = [task.client_first_name, task.client_last_name].filter(Boolean).join(' ') || 'Client';
  const clientInitials = `${task.client_first_name?.[0] || ''}${task.client_last_name?.[0] || ''}`.toUpperCase() || 'C';

  const isVE = staffRole === 'video_editor';
  const isMonteur = staffRole === 'monteur';

  const isDerushPhase = isVE && ['shot', 'derush_pending', 'derush_done'].includes(task.status || '');
  const isVEReviewPhase = isVE && (task.status === 'editing_review' || task.status === 'redelivery_review');
  const isReviewTask = task.status === 'editing_review' || task.status === 'redelivery_review';
  const showThumbnail = isReviewTask && thumbnail;

  // Hide channels for staff roles
  const showChannels = staffRole !== 'video_editor' && staffRole !== 'monteur';

  // Check derush files when VE expands the card during derush phase
  useEffect(() => {
    if (isExpanded && isVE && isDerushPhase && task.shooting_id) {
      hasDerushFiles(task.shooting_id, task.id).then(result => {
        setHasFiles(result);
      });
    }
  }, [isExpanded, isVE, isDerushPhase, task.shooting_id, task.id]);

  // Refresh file check after upload
  const handleUploadComplete = () => {
    if (task.shooting_id) {
      hasDerushFiles(task.shooting_id, task.id).then(setHasFiles);
    }
  };

  const handleSendToMontage = async () => {
    setIsValidating(true);
    const result = await advanceVEStatus(task.id);
    setIsValidating(false);
    if (result.success) {
      toast.success('Envoyée au monteur');
      setIsDone(true);
      setIsExpanded(false);
    } else {
      toast.error(result.error || 'Erreur');
    }
  };

  const handleDownloadSources = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!task.shooting_id) {
      toast.error('Pas de shooting associé');
      return;
    }
    setIsDownloading(true);
    try {
      const files = await getDerushFiles(task.shooting_id);
      if (files.length === 0) {
        toast.error('Aucun fichier dans le dossier Derush');
        return;
      }
      for (const file of files) {
        const result = await getS3DownloadUrl(file.s3_key);
        if (result.success && result.data?.url) {
          window.open(result.data.url, '_blank');
        }
      }
    } catch {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenVEUpload = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVEUploadOpen(true);
  };

  const handleVEValidate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsValidating(true);
    const result = await advanceVEStatus(task.id);
    setIsValidating(false);
    if (result.success) {
      const label = result.newStatus === 'editing_pending' ? 'Envoyée au monteur' : 'Envoyée en validation client';
      toast.success(label);
      setIsDone(true);
      setIsExpanded(false);
    } else {
      toast.error(result.error || 'Erreur');
    }
  };

  const handleViewFolder = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!task.shooting_id) {
      toast.error('Pas de shooting associé');
      return;
    }
    const folder = await getSubfolder(task.shooting_id, 'Derush', task.id);
    if (!folder) {
      toast.error('Dossier introuvable');
      return;
    }
    navigate(`/app/assets?folder=${folder.id}`);
  };


  const handleCardClick = () => {
    if (isDone) return;
    if (isMonteur) {
      setMonteurDetailOpen(true);
    } else if (isVE && isVEReviewPhase) {
      setVEReviewOpen(true);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  // Render CTA buttons based on staff role
  const renderCtaButtons = () => {
    if (isMonteur) {
      return (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={handleViewFolder}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/5 border border-white/20 text-white/60 text-[11px] font-medium hover:bg-white/10 hover:border-white/30 transition-all"
          >
            <Eye className="h-3.5 w-3.5" />
            <span>Voir</span>
          </button>
          <button
            onClick={handleDownloadSources}
            disabled={isDownloading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-[11px] font-medium hover:bg-blue-500/20 hover:border-blue-500/50 transition-all disabled:opacity-50"
          >
            {isDownloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      );
    }

    if (isVE) {
      return (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isDerushPhase && (
            <button
              onClick={handleOpenVEUpload}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-warning/10 border border-warning/30 text-warning text-[11px] font-medium hover:bg-warning/20 hover:border-warning/50 transition-all"
            >
              <Upload className="h-3.5 w-3.5" />
              <span>Derush</span>
            </button>
          )}
          {isVEReviewPhase && (
            <button
              onClick={handleVEValidate}
              disabled={isValidating}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[11px] font-medium hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all disabled:opacity-50"
            >
              {isValidating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle className="h-3.5 w-3.5" />
              )}
              <span>Valider</span>
            </button>
          )}
        </div>
      );
    }

    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/30 text-xs font-medium cursor-default">
        <span>En attente</span>
      </span>
    );
  };

  return (
    <>
      <button
        className={`group glass border rounded-lg p-3 transition-all duration-300 ${getCardBorderClass(isDone, isExpanded)} text-left`}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        disabled={isDone}
        aria-expanded={isExpanded}
      >
        {/* Header: Client info */}
        <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0">
            <Avatar className="h-5 w-5">
              <AvatarImage src={task.client_avatar_url || undefined} alt={clientName} />
              <AvatarFallback className="text-[9px] bg-white/10">{clientInitials}</AvatarFallback>
            </Avatar>
            <span className="text-xs text-white/70 font-medium truncate">{clientName}</span>
          </div>
          <span className={`text-[10px] uppercase tracking-wider flex-shrink-0 ${isDone ? 'text-emerald-400' : getStatusColor(task.status, staffRole)}`}>
            {isDone ? '✓ Envoyé' : getStatusLabel(task.status, staffRole)}
          </span>
        </div>

        <div className="flex gap-3 items-start">
          {/* Thumbnail - only for review tasks */}
          {showThumbnail && (
            <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-white/5">
              <SignedImage
                src={thumbnail}
                alt={task.title || 'Video thumbnail'}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="flex-1 min-w-0 space-y-2.5 py-1">
            {/* Title & CTA */}
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm text-white tracking-tight leading-snug line-clamp-2 flex-1">
                {task.title || 'Vidéo sans titre'}
              </h3>

              {renderCtaButtons()}
            </div>

            {/* Channels */}
            {showChannels && (
              <div className="flex items-center gap-1">
                {task.channels.slice(0, 4).map((channel) => {
                  const IconComponent = getChannelIcon(channel as Channel);
                  return IconComponent ? (
                    <IconComponent key={channel} className="h-4 w-4 text-white/40" />
                  ) : null;
                })}
              </div>
            )}
          </div>
        </div>

        {/* VE assigné */}
        {/* Footer: VE + badges */}
        {isMonteur && (
          <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-white/5">
            {task.ve_first_name ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-white/40">VE :</span>
                <span className="text-xs text-white/60 font-medium">
                  {task.ve_first_name} {task.ve_last_name}
                </span>
              </div>
            ) : <div />}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {task.content_type && task.content_type !== 'standard' && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-white/20 text-white/50 uppercase tracking-wider">
                  {CONTENT_TYPE_LABELS[task.content_type] || task.content_type}
                </Badge>
              )}
              {task.virality_predicted && (
                <Badge variant="outline" className={`text-[9px] px-1.5 py-0 h-4 uppercase tracking-wider ${getViralityBadgeClass(task.virality_predicted)}`}>
                  {task.virality_predicted}
                </Badge>
              )}
              {task.is_multihook && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-purple-500/40 text-purple-400 uppercase tracking-wider">
                  Multihook
                </Badge>
              )}
            </div>
          </div>
        )}

        {isExpanded && (
          <section
            className="mt-3 pt-3 border-t border-white/10 space-y-3"
            aria-labelledby="video-details-heading"
          >
            {/* Video info */}
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              {task.content_type && (
                <div>
                  <span className="text-white/40">Type</span>
                  <p className="text-white/80">{CONTENT_TYPE_LABELS[task.content_type] || task.content_type}</p>
                </div>
              )}
              {task.earliest_publication_date && (
                <div>
                  <span className="text-white/40">Publication</span>
                  <p className="text-white/80">{new Date(task.earliest_publication_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              )}
              {isVE && task.derush_deadline && (
                <div>
                  <span className="text-white/40">Échéance dérush</span>
                  <p className={getDeadlineColor(task.derush_deadline)}>{new Date(task.derush_deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p>
                </div>
              )}
              {isMonteur && task.delivery_deadline && (
                <div>
                  <span className="text-white/40">Livraison</span>
                  <p className={getDeadlineColor(task.delivery_deadline)}>{new Date(task.delivery_deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2">
              {isVE && isDerushPhase && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setVEUploadOpen(true)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-warning/10 border border-warning/30 text-warning text-xs font-medium hover:bg-warning/20 hover:border-warning/50 transition-all"
                  >
                    <Upload className="h-4 w-4" />
                    Uploader Derush
                  </button>
                  {hasFiles && (
                    <button
                      onClick={handleSendToMontage}
                      disabled={isValidating}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all disabled:opacity-50"
                    >
                      {isValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Envoyer en montage
                    </button>
                  )}
                </div>
              )}
              {isVE && isVEReviewPhase && (
                <button
                  onClick={handleVEValidate}
                  disabled={isValidating}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all disabled:opacity-50"
                >
                  {isValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Valider → Envoyer au monteur
                </button>
              )}
            </div>
          </section>
        )}
      </button>

      {/* Monteur detail dialog */}
      {isMonteur && (
        <MonteurDetailDialog
          open={monteurDetailOpen}
          onOpenChange={setMonteurDetailOpen}
          task={task}
          onDone={() => {
            setIsDone(true);
            setMonteurDetailOpen(false);
          }}
        />
      )}

      {/* VE upload dialog */}
      {isVE && task.shooting_id && (
        <VEUploadDialog
          open={veUploadOpen}
          onOpenChange={setVEUploadOpen}
          videoContentId={task.id}
          shootingId={task.shooting_id}
          videoTitle={task.title}
          onComplete={handleUploadComplete}
        />
      )}

      {/* VE review dialog */}
      {isVE && (
        <VEReviewDialog
          open={veReviewOpen}
          onOpenChange={setVEReviewOpen}
          task={task}
          onDone={() => {
            setIsDone(true);
            setVEReviewOpen(false);
          }}
        />
      )}
    </>
  );
}
